import {
    type CreateExtArgs,
    type DmApis,
    type Extension,
    type ExtensionAPI,
    type CreateExtension,
    pointerUtils
} from '@wix/document-manager-core'
import type {AppController, AppControllerData, CompRef, Pointer} from '@wix/document-services-types'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import {DATA_TYPES} from '../constants/constants'
import {stripHashIfExists} from '../utils/refArrayUtils'
import type {StructureExtensionAPI} from './structure'

const {getPointer} = pointerUtils

interface ControllersAPI extends ExtensionAPI {
    create(controllerId: string, compPointer: CompRef, controllerData: AppControllerData): Pointer
    getControllerToAddRef(id?: string): Pointer
    getAvailableScopedControllers(componentRef: CompRef): Pointer[]
}

interface ControllersExtensionAPI extends ExtensionAPI {
    controllers: ControllersAPI
}

const createExtension: CreateExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, dal, pointers}: CreateExtArgs): ControllersExtensionAPI => {
        const {dataModel} = extensionAPI as DataModelExtensionAPI

        const createControllerList = (compPointer: CompRef): Pointer => {
            const controllerListDataItem = dal.schema.createItemAccordingToSchema(
                'ControllerList',
                DATA_TYPES.controllers,
                {
                    controllers: []
                }
            )
            return dataModel.components.addItem(compPointer, DATA_TYPES.controllers, controllerListDataItem)
        }

        const addControllerToControllerList = (compPointer: CompRef, controllerPointer: Pointer) => {
            let controllerListPointer = dataModel.components.getItemPointer(compPointer, DATA_TYPES.controllers)
            if (!controllerListPointer) {
                controllerListPointer = createControllerList(compPointer)
            }
            const controllersList = dal.get(controllerListPointer)
            const newControllerList = {
                ...controllersList,
                controllers: [...controllersList.controllers, `#${controllerPointer.id}`]
            }
            dal.set(controllerListPointer, newControllerList)
        }

        const createController = (controllerId: string, controllerData: AppControllerData, pageId: string) => {
            const controllerDataItem = dal.schema.createItemAccordingToSchema(
                'AppController',
                DATA_TYPES.data,
                controllerData
            )
            return dataModel.addItem(controllerDataItem, DATA_TYPES.data, pageId, controllerId)
        }

        const getDefinedControllersIds = (compPointer: Pointer): string[] => {
            const controllerListPointer = dataModel.components.getItemPointer(compPointer, DATA_TYPES.controllers)

            if (controllerListPointer) {
                const controllerList = dal.get(controllerListPointer)
                if (controllerList?.controllers) {
                    return controllerList.controllers.map(stripHashIfExists) as string[]
                }
            }
            return []
        }

        const create = (controllerId: string, compPointer: CompRef, controllerData: AppControllerData): Pointer => {
            if (!dal.has(compPointer)) {
                throw new Error('Component does not exist')
            }
            const pageId = pointers.structure.getPageOfComponent(compPointer).id
            const controllerPointer = createController(controllerId, controllerData, pageId)
            addControllerToControllerList(compPointer, controllerPointer)
            return controllerPointer
        }

        const getControllerToAddRef = (id?: string) => {
            const customId = id || dataModel.generateNewId(DATA_TYPES.data)
            return pointers.data.getDataItem(customId, DATA_TYPES.data)
        }

        const getAvailableScopedControllers = (compPointer: CompRef): Pointer[] => {
            const {components} = extensionAPI as StructureExtensionAPI
            const pageId = pointers.structure.getPageOfComponent(compPointer).id
            const ancestors = [compPointer, ...components.getAncestors(compPointer, true)]

            const allControllerIds = new Set<string>()
            ancestors.forEach((ancestor: Pointer) => {
                const definedControllersIds = getDefinedControllersIds(ancestor)
                definedControllersIds.forEach(controllerId => allControllerIds.add(controllerId))
            })

            return Array.from(allControllerIds).map((controllerId: string) =>
                getPointer(controllerId, DATA_TYPES.data, {pageId})
            )
        }
        return {
            controllers: {
                create,
                getControllerToAddRef,
                getAvailableScopedControllers
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {controllers} = extensionAPI as ControllersExtensionAPI
        return {
            controllers: {
                create(controllerPointer: Pointer, compPointer: CompRef, controllerData: AppController): Pointer {
                    return controllers.create(controllerPointer.id, compPointer, controllerData)
                },
                getControllerToAddRef(compPointer: CompRef, controllerData: AppController, id?: string): Pointer {
                    return controllers.getControllerToAddRef(id)
                },
                getAvailableScopedControllers: controllers.getAvailableScopedControllers
            }
        }
    }

    return {
        name: 'controllers',
        createExtensionAPI,
        createPublicAPI,
        dependencies: new Set(['dataModel', 'structure'])
    }
}

export {createExtension, ControllersExtensionAPI, ControllersAPI}
